import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Star,
  ArrowRight,
  Menu,
  X,
  BookOpen,
  Users,
  Zap,
  Image,
  TrendingUp
} from 'lucide-react';
import { Section } from '../components/layout';
import { useScrollPosition } from '../hooks';
import { FEATURES, STATS, TESTIMONIALS } from '../constants/content';

const COPIES_PER_SET = 8;

const Landing = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isPastHero, setIsPastHero] = useState(false);
  const isScrolled = useScrollPosition();

  const demoDestinations = useMemo(
    () => [
      { label: 'Study', path: '/app/study-demo', icon: BookOpen },
      { label: 'Challenges', path: '/app/challenges-demo', icon: Zap },
      { label: 'Community', path: '/app/community-demo', icon: Users },
      { label: 'Pin Up', path: '/app/pinup-demo', icon: Image },
      { label: 'Progress', path: '/app/progress-demo', icon: TrendingUp }
    ],
    []
  );

  const renderMarqueeSet = (keyPrefix) =>
    Array.from({ length: COPIES_PER_SET }).flatMap((_, repeatIdx) =>
      demoDestinations.map((item, idx) => {
      const Icon = item.icon;
      return (
        <Link
            key={`${keyPrefix}-${repeatIdx}-${item.label}-${idx}`}
          to={item.path}
          className="marquee-card"
            aria-label={`Explore the ${item.label} demo`}
            title={`Explore the ${item.label} demo`}
        >
          <div className="marquee-card__icon">
              <Icon className="w-[67px] h-[67px] text-[#AC5757]" aria-hidden="true" />
          </div>
        </Link>
      );
      })
    );

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      // Check if we've scrolled past the hero image
      const heroSection = document.querySelector('.hero-section');
      if (heroSection) {
        const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
        setIsPastHero(window.scrollY > heroBottom - 100);
      }

      // Get all section positions
      const featuresSection = document.getElementById('features');
      const howItWorksSection = document.getElementById('how-it-works');
      const testimonialsSection = document.getElementById('testimonials');

      // Determine active section based on ranges between sections
      if (featuresSection && howItWorksSection && testimonialsSection) {
        const featuresStart = featuresSection.offsetTop;
        const howItWorksStart = howItWorksSection.offsetTop;
        const testimonialsStart = testimonialsSection.offsetTop;

        if (scrollPosition >= testimonialsStart) {
          // From testimonials section onwards
          setActiveSection('testimonials');
        } else if (scrollPosition >= howItWorksStart - 200) {
          // From how-it-works section to testimonials section (includes marquees)
          setActiveSection('how-it-works');
        } else if (scrollPosition >= featuresStart) {
          // From features section to how-it-works section
          setActiveSection('features');
          } else {
          // Before features section
            setActiveSection('');
        }
      }
    };

    // Handle smooth scrolling for navigation links
    const handleNavClick = (e) => {
      const href = e.target.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const targetId = href.substring(1);
        
        if (targetId === 'how-it-works') {
          // For "How It Works", scroll to show both marquees
          const topMarquee = document.querySelector('.top-marquee');
          if (topMarquee) {
            // Set active section immediately
            setActiveSection('how-it-works');
            // Scroll to the very top of the marquee section
            const scrollTop = topMarquee.offsetTop - 20; // 20px offset from the very top
            window.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          }
        } else {
          // For other sections, use normal scroll behavior
          const targetElement = document.getElementById(targetId);
          if (targetElement) {
            targetElement.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleNavClick);
    
    // Multi-card hover effect
    const cardsContainer = document.getElementById('feature-cards');
    if (cardsContainer) {
      const cards = Array.from(cardsContainer.querySelectorAll('.feature-card'));

      const updatePositions = (clientX, clientY) => {
        cards.forEach((card) => {
          const rect = card.getBoundingClientRect();
          const x = clientX - rect.left;
          const y = clientY - rect.top;
          card.style.setProperty('--x', x + 'px');
          card.style.setProperty('--y', y + 'px');
        });
      };

      const handleMouseMove = (e) => {
        updatePositions(e.clientX, e.clientY);
      };

      const handleTouchMove = (e) => {
        const t = e.touches[0];
        if (!t) return;
        updatePositions(t.clientX, t.clientY);
      };

      const hideAll = () => {
        cards.forEach(card => {
          card.style.setProperty('--x', '-100px');
          card.style.setProperty('--y', '-100px');
        });
      };

      cardsContainer.addEventListener('mousemove', handleMouseMove);
      cardsContainer.addEventListener('touchmove', handleTouchMove, { passive: true });
      cardsContainer.addEventListener('mouseleave', hideAll);

      return () => {
        window.removeEventListener('scroll', handleScroll);
        document.removeEventListener('click', handleNavClick);
        cardsContainer.removeEventListener('mousemove', handleMouseMove);
        cardsContainer.removeEventListener('touchmove', handleTouchMove);
        cardsContainer.removeEventListener('mouseleave', hideAll);
      };
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleNavClick);
    };
  }, []);

  return (
    <div style={{ 
      backgroundImage: 'url(/assets/landingbackground.png?v=6)', 
      backgroundSize: 'cover', 
      backgroundPosition: 'center top', 
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
      fontFamily: 'Arial, Helvetica, sans-serif' // added: use Arial across the landing page
    }}>
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'backdrop-blur-md shadow-sm' : ''
      } bg-[#F3E9E7]/95`}>
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Mobile Menu Button - Left Side */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-700"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Desktop Nav - Right Side */}
            <div className="hidden md:flex items-center gap-4 ml-auto">
              <a 
                href="#features" 
                className={`transition-colors font-medium px-4 py-2 rounded-lg text-sm ${
                  !isPastHero 
                    ? 'text-gray-700 hover:text-[#AC5757] bg-white hover:bg-gray-100' 
                    : activeSection === 'features' 
                      ? 'text-black hover:text-gray-600 bg-white hover:bg-gray-100' 
                      : 'text-[#AC5757] bg-red-100 cursor-default'
                }`}
              >
                Features
              </a>
              <a 
                href="#how-it-works" 
                className={`transition-colors font-medium px-4 py-2 rounded-lg text-sm ${
                  !isPastHero 
                    ? 'text-gray-700 hover:text-[#AC5757] bg-white hover:bg-gray-100' 
                    : activeSection === 'how-it-works' 
                      ? 'text-black hover:text-gray-600 bg-white hover:bg-gray-100' 
                      : 'text-[#AC5757] bg-red-100 cursor-default'
                }`}
              >
                How it Works
              </a>
              <a 
                href="#testimonials" 
                className={`transition-colors font-medium px-4 py-2 rounded-lg text-sm ${
                  !isPastHero 
                    ? 'text-gray-700 hover:text-[#AC5757] bg-white hover:bg-gray-100' 
                    : activeSection === 'testimonials' 
                      ? 'text-black hover:text-gray-600 bg-white hover:bg-gray-100' 
                      : 'text-[#AC5757] bg-red-100 cursor-default'
                }`}
              >
                Testimonials
              </a>
              <Link 
                to="/login" 
                className={`transition-colors font-medium text-sm px-2 ${
                  !isPastHero 
                    ? 'text-[#AC5757] hover:text-[#8A4A4A]' 
                    : activeSection ? 'text-[#AC5757]' : 'text-[#AC5757] hover:text-[#8A4A4A]'
                }`}
              >
                Log In
              </Link>
              <Link 
                to="/signup" 
                className="px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center text-sm bg-[#AC5757] text-white hover:bg-[#8A4A4A]"
              >
                Sign Up
              </Link>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-[#F3E9E7]">
              <div className="px-4 py-4 space-y-3">
                <a 
                  href="#features" 
                  className={`block transition-colors font-medium px-3 py-2 rounded-lg ${
                    !isPastHero 
                      ? 'text-gray-700 hover:text-[#AC5757] bg-white hover:bg-gray-100' 
                      : activeSection === 'features' 
                        ? 'text-black hover:text-gray-600 bg-white hover:bg-gray-100' 
                        : 'text-[#AC5757] bg-red-100 cursor-default'
                  }`}
                >
                  Features
                </a>
                <a 
                  href="#how-it-works" 
                  className={`block transition-colors font-medium px-3 py-2 rounded-lg ${
                    !isPastHero 
                      ? 'text-gray-700 hover:text-[#AC5757] bg-white hover:bg-gray-100' 
                      : activeSection === 'how-it-works' 
                        ? 'text-black hover:text-gray-600 bg-white hover:bg-gray-100' 
                        : 'text-[#AC5757] bg-red-100 cursor-default'
                  }`}
                >
                  How it Works
                </a>
                <a 
                  href="#testimonials" 
                  className={`block transition-colors font-medium px-3 py-2 rounded-lg ${
                    !isPastHero 
                      ? 'text-gray-700 hover:text-[#AC5757] bg-white hover:bg-gray-100' 
                      : activeSection === 'testimonials' 
                        ? 'text-black hover:text-gray-600 bg-white hover:bg-gray-100' 
                        : 'text-[#AC5757] bg-red-100 cursor-default'
                  }`}
                >
                  Testimonials
                </a>
                <Link 
                  to="/login" 
                  className={`block transition-colors font-medium text-center ${
                    !isPastHero 
                      ? 'text-[#AC5757] hover:text-[#8A4A4A]' 
                      : activeSection ? 'text-[#AC5757]' : 'text-[#AC5757] hover:text-[#8A4A4A]'
                  }`}
                >
                  Log In
                </Link>
                <Link 
                  to="/signup" 
                  className="block px-6 py-3 rounded-lg font-semibold text-center transition-colors flex items-center justify-center mx-auto text-sm bg-[#AC5757] text-white hover:bg-[#8A4A4A]"
                >
                  Sign Up
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <Section background="bg-transparent" padding="pt-60 sm:pt-40 pb-20" className="hero-section">
        
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#AC5757] rounded-full filter blur-3xl opacity-10" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#AC5757] rounded-full filter blur-3xl opacity-10" />
        
        <div className="relative z-10 text-center mt-9 sm:mt-16 px-4">
          {/* AXORA Logo */}
          <div className="mb-8">
            <span className="font-instrument-sans font-bold text-[#5c1a1a] whitespace-nowrap" style={{ fontSize: 'clamp(3.6rem, 9vw + 1.8rem, 10.8rem)' }}>
              AXORA
            </span>
          </div>
          
          <div className="mt-[100px]">
            <h1 className="font-judson font-bold text-white mb-4 sm:mb-6 leading-tight drop-shadow-lg whitespace-nowrap" style={{ fontSize: 'clamp(2rem, 5vw + 1rem, 4.05rem)' }}>
              Learn. Build.
              <span className="text-[#AC5757]"> Level Up.</span>
            </h1>
            
            <p className="text-black bg-white mb-6 sm:mb-8 max-w-2xl mx-auto rounded-lg shadow-lg" style={{ fontSize: 'clamp(1.365rem, 2.34vw + 0.78rem, 1.56rem)', padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1rem, 3vw, 2rem)' }}>
              <span className="font-normal text-lg sm:text-xl">AI-guided continued professional development for Engineers and Architects.</span>
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-8 sm:mb-12">
              <Link to="/login?role=learner" className="inline-flex items-center justify-center gap-2 bg-[#AC5757] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-[#8A4A4A] transition-colors shadow-lg text-sm sm:text-base">
                Launch Learner Demo <ArrowRight size={18} />
              </Link>
              <Link to="/educator-login" className="inline-flex items-center justify-center gap-2 bg-[#AC5757] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-[#8A4A4A] transition-colors shadow-lg text-sm sm:text-base">
                Launch Educator Demo <ArrowRight size={18} />
              </Link>
            </div>
          </div>

        </div>
      </Section>

      {/* Features Section */}
      <Section id="features" background="bg-[#F4E9E7]">
        {/* Stats Preview - Now positioned above the title */}
        <div className="max-w-4xl mx-auto px-4 -mt-12 mb-12">
          <div className="p-4 md:p-6 border-8 border-[#814544] rounded-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
              {STATS.map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#8C4947]">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-gray-600 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Axora summary section*/}
        <div className="mb-16 text-center">
          <h2 className="font-judson text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Master Your Field with <span className="font-oswald font-medium text-[#5C1A1A]">AXORA</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Revolutionary features that transform education through artificial intelligence and immersive virtual reality
          </p>
        </div>

        <div className="feature-cards" id="feature-cards">
          {FEATURES.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <div key={idx} className="feature-card" tabIndex="0">
                <div className="feature-card__border"></div>
                <div className="feature-card__content">
                  <div>
                    <div className="w-16 h-16 bg-gradient-to-br from-[#AC5757]/20 to-[#AC5757]/5 rounded-none flex items-center justify-center mb-4 mx-auto">
                  <Icon className="text-[#AC5757]" size={28} />
                    </div>
                    <div className="text-lg font-bold text-gray-900 mb-2">{feature.title}</div>
                    <div className="text-sm text-gray-600 leading-relaxed">{feature.description}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Icon Marquee - Above How It Works */}
      <div className="bg-[#F4E9E7] py-8 top-marquee">
        {/* Icon Marquee - Full Screen Width */}
        <div className="relative w-screen -mx-6 overflow-hidden">
          <div className="animate-marquee">
            <div className="marquee-content">
              {renderMarqueeSet('loop-a')}
            </div>
            <div className="marquee-content" aria-hidden="true">
              {renderMarqueeSet('loop-b')}
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <Section id="how-it-works" background="bg-[#F4E9E7]" className="py-14">
        <Section.Header 
          title="How It Works"
          subtitle="Get started in minutes and transform your learning experience"
        />

        <div className="grid md:grid-cols-3 gap-8">
          {[
            { step: 1, title: 'Share Your Background', desc: 'Set your role, goals, and interests so the platform can tailor your CPD pathway.' },
            { step: 2, title: 'Get Personalized Learning Paths', desc: 'AI curates accredited courses, resources, and skill-building tasks based on your level.' },
            { step: 3, title: 'Develop, Track & Grow', desc: 'Earn CPD credits, build competencies, and unlock opportunities through real-world challenges.' }
          ].map((item, idx) => (
            <div key={idx} className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#AC5757] to-[#8A4A4A] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <span className="text-white font-black" style={{ 
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  letterSpacing: '1px',
                  fontSize: '36px'
                }}>
                  {item.step}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Bottom Icon Marquee - Outside Section for full width */}
      <div className="bg-[#F4E9E7] py-8">
        <div className="relative w-screen -mx-6 overflow-hidden">
          <div className="animate-marquee">
            <div className="marquee-content">
              {renderMarqueeSet('loop-c')}
            </div>
            <div className="marquee-content" aria-hidden="true">
              {renderMarqueeSet('loop-d')}
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <Section id="testimonials" background="bg-gray-100">
        <Section.Header 
          title="Loved by Students & Educators"
          subtitle="Join thousands who've transformed their learning experience"
        />

        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((testimonial, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="text-[#AC5757]" size={20} fill="currentColor" />
                ))}
              </div>
              <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
              <div>
                <div className="font-semibold text-gray-900">{testimonial.name}</div>
                <div className="text-sm text-gray-600">{testimonial.role}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="bg-[#AC5757]" padding="py-20">
        <div className="text-center text-white">
          <h2 className="font-judson text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Profession? 
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join AXORA today
          </p>
          
          {/* Demo Buttons */}
          <div className="flex justify-center gap-4 mb-8">
            <Link to="/login?role=learner" className="inline-flex items-center gap-2 bg-white text-[#AC5757] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors">
              Launch Learner Demo <ArrowRight size={20} />
            </Link>
            <Link to="/educator-login" className="inline-flex items-center gap-2 bg-white text-[#AC5757] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors">
              Launch Educator Demo <ArrowRight size={20} />
            </Link>
          </div>
          
        </div>
      </Section>

      {/* Bottom Card Section */}
      <div className="bg-[#AC5757] relative min-h-[600px]">
        <div className="bg-[#F3E9E7] rounded-t-[60px] min-h-[568px] w-full relative z-10 mx-auto max-w-[calc(100%-50px)] border-8 border-[#AC5757]">
      <div className="max-w-6xl mx-auto px-6 py-16 rounded-[50px]">
          </div>
          
          {/* Features Section - Left */}
          <div className="absolute top-5 right-[250px] flex items-start gap-2">
            {/* Vertical FEATURES Text */}
            <div className="flex flex-col items-center">
              <div className="font-archivo-black font-black text-[0.84rem] text-[#5c1a1a] leading-tight">
                F<br/>E<br/>A<br/>T<br/>U<br/>R<br/>E<br/>S
              </div>
            </div>
            
            {/* Clickable Links - Aligned with each letter */}
            <div className="flex flex-col leading-tight">
              <a href="/app/study-demo" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center -mt-[1px]">Study</a>
              <a href="/app/community-demo" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">Community</a>
              <a href="/app/challenges-demo" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">Challenges</a>
              <a href="/app/pinup-demo" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">Pinup</a>
            </div>
          </div>

          {/* Contents Section - Middle */}
          <div className="absolute top-5 right-[140px] flex items-start gap-2">
            {/* Vertical CONTENTS Text */}
            <div className="flex flex-col items-center">
              <div className="font-archivo-black font-black text-[0.84rem] text-[#5c1a1a] leading-tight">
                C<br/>O<br/>N<br/>T<br/>E<br/>N<br/>T<br/>S
              </div>
            </div>
            
            {/* Clickable Links - Aligned with each letter */}
            <div className="flex flex-col leading-tight">
              <a href="#about" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center -mt-[1px]">About Us</a>
              <a href="#how-it-works" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">How it Works</a>
              <a href="/pricing" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">Pricing</a>
              <a href="#testimonials" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">Testimonials</a>
            </div>
          </div>

          {/* Links Section - Right */}
          <div className="absolute top-5 right-[40px] flex items-start gap-2">
            {/* Vertical LINKS Text */}
            <div className="flex flex-col items-center">
              <div className="font-archivo-black font-black text-[0.84rem] text-[#5c1a1a] leading-tight">
                L<br/>I<br/>N<br/>K<br/>S
              </div>
            </div>
            
            {/* Clickable Links - Aligned with each letter */}
            <div className="flex flex-col leading-tight">
              <a href="#contact" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">Contact</a>
              <a href="#support" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">Support</a>
              <a href="#blog" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">Blog</a>
              <a href="#careers" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">Careers</a>
              <a href="#privacy" className="font-abel text-[0.84rem] text-[#5c1a1a] hover:text-[#4a1414] transition-colors h-[1.2rem] flex items-center">Privacy</a>
            </div>
          </div>

          {/* Help Section */}
          <div className="absolute top-[250px] left-[60px] text-left">
            <h3 className="font-archivo-black font-bold text-lg text-[#AC5757] mb-4 text-left">
              <div>NEED HELP WITH SIGNING UP?</div>
              <div>GET IN CONTACT WITH OUR TEAM.</div>
            </h3>
            
            {/* Contact Cards */}
            <div className="flex gap-4 justify-center">
              {/* Email Card */}
              <a href="mailto:help@axora.com" className="bg-[#AC5757] rounded-[5px] px-2 py-2 hover:bg-[#8A4A4A] transition-colors">
                <p className="font-abel text-sm text-white">help@axora.com</p>
              </a>
              
              {/* Phone Card */}
              <a href="tel:+97334536789" className="bg-[#AC5757] rounded-[5px] px-2 py-2 hover:bg-[#8A4A4A] transition-colors">
                <p className="font-abel text-sm text-white">+973 34536789</p>
              </a>
            </div>
          </div>

          <div className="absolute -bottom-2 left-0 right-0 text-center">
            <h1 className="font-instrument-sans text-8xl md:text-[10rem] font-bold text-[#5c1a1a] leading-none relative">
              <div className="absolute top-[55%] md:top-[56%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[32.4%] md:w-[14.875%] h-[10px] md:h-[12px] bg-[#5c1a1a] z-10"></div>
              <div className="absolute top-[55%] md:top-[55%] left-0 transform translate-y-[4.5px] md:translate-y-[9px] -translate-y-1/2 w-[19.44%] md:w-[35%] h-[10px] md:h-[12px] bg-[#5c1a1a] z-10"></div>
              <div className="absolute top-[55%] md:top-[55%] right-0 transform translate-y-[4.5px] md:translate-y-[9px] -translate-y-1/2 w-[19.44%] md:w-[35%] h-[10px] md:h-[12px] bg-[#5c1a1a] z-10"></div>
              AXORA
            </h1>
          </div>

          {/* Scroll to Top Button */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="absolute top-[55%] right-[30px] md:right-[170px] transform translate-y-[4.5px] -translate-y-1/2 translate-y-[77px] w-14 h-14 bg-[#AC5757] rounded-full flex items-center justify-center shadow-lg hover:bg-[#8A4A4A] transition-colors z-20"
            aria-label="Scroll to top"
          >
            <svg
              className="w-6 h-6 text-[#F3E9E7]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {/* Up arrow */}
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12l7-7 7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Landing;